import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    const rowsToRemove = cartItemRowToRemove
      ? [
          cartItemRowToRemove,
          // Get all nested lines of the row to remove
          ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
        ]
      : [];

    const isEmptyCart = rowsToRemove.length > 0 && rowsToRemove.length == this.refs.cartItemRows.length;

    // Focus only needs rescuing when it sits on a row that is about to go.
    const active = document.activeElement;
    const hadFocusInRemovedRow = rowsToRemove.some((row) => active instanceof Node && row.contains(active));
    const focusCandidateKeys = this.#getFocusCandidateKeys(cartItemRowToRemove, rowsToRemove);

    // The rows animate out straight away, but they are only taken out of the
    // page, and the removal only announced, once the server confirms. If the
    // request fails the rows come back and the shopper is told why.
    if (!prefersReducedMotion()) {
      rowsToRemove.forEach((row) => {
        row.style.setProperty('--row-height', `${row.clientHeight}px`);
        row.classList.add('removing');
      });
    }

    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    }).then((ok) => {
      if (!ok) {
        rowsToRemove.forEach((row) => row.classList.remove('removing'));
        return;
      }

      // Announce the removal to screen readers. The localized string comes from
      // the data-remove-announcement attribute rendered by main-cart.liquid and
      // header-actions.liquid.
      this.#announce(this.dataset.removeAnnouncement);

      // Template IDs are scoped to drawer vs page to stay unique.
      const templateId = this.isDrawer ? 'empty-cart-drawer-template' : 'empty-cart-page-template';
      const template = document.getElementById(templateId);
      if (isEmptyCart && template instanceof HTMLTemplateElement && this.isConnected && this.querySelector('[ref="cartItemRows[]"], .cart-items__table-row')) {
        const clone = document.importNode(template.content, true);
        startViewTransition(() => {
          this.replaceChildren(clone);
          if (hadFocusInRemovedRow) this.#restoreFocusAfterRemoval([], rowsToRemove);
        }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);
        return;
      }

      if (hadFocusInRemovedRow) this.#restoreFocusAfterRemoval(focusCandidateKeys, rowsToRemove);

      rowsToRemove.forEach((row) => {
        if (!row.isConnected) return;
        if (prefersReducedMotion() || !row.classList.contains('removing')) return row.remove();
        onAnimationEnd(row, () => row.remove());
      });
    });
  }

  /**
   * Lists the keys of the rows that can take focus once a line is removed:
   * the rows after it first, then the rows before it, nearest first.
   * @param {HTMLTableRowElement | undefined} removedRow
   * @param {HTMLTableRowElement[]} rowsToRemove
   * @returns {string[]}
   */
  #getFocusCandidateKeys(removedRow, rowsToRemove) {
    const rows = this.refs.cartItemRows || [];
    const index = removedRow ? rows.indexOf(removedRow) : -1;
    if (index === -1) return [];
    const isKept = (/** @type {HTMLTableRowElement} */ row) => !rowsToRemove.includes(row) && !!row.dataset.key;
    const after = rows.slice(index + 1).filter(isKept);
    const before = rows.slice(0, index).filter(isKept).reverse();
    return [...after, ...before].map((row) => row.dataset.key || '');
  }

  /**
   * Moves focus to the nearest remaining line, or to the empty bag heading,
   * or to the bag's close button or title, so keyboard users keep their place.
   * @param {string[]} candidateKeys
   * @param {HTMLTableRowElement[]} removedRows
   */
  #restoreFocusAfterRemoval(candidateKeys, removedRows) {
    // Leave focus alone if the shopper has already moved on while the request ran.
    const current = document.activeElement;
    const focusIsLost =
      !current ||
      current === document.body ||
      !current.isConnected ||
      removedRows.some((row) => row.contains(current));
    if (!focusIsLost) return;

    const root = this.isConnected
      ? this
      : document.querySelector(`cart-items-component[data-section-id="${this.dataset.sectionId}"]`);
    if (!(root instanceof HTMLElement)) return;

    const isVisible = (/** @type {Element | null} */ el) =>
      el instanceof HTMLElement && !el.hidden && el.getClientRects().length > 0;

    const rows = /** @type {HTMLTableRowElement[]} */ (Array.from(root.querySelectorAll('tr[data-key]')));
    for (const key of candidateKeys) {
      const row = rows.find((candidate) => candidate.dataset.key === key && !candidate.classList.contains('removing'));
      if (!row) continue;
      const target = [
        row.querySelector('.cart-items__remove'),
        row.querySelector('quantity-selector-component input'),
        row.querySelector('.cart-items__name a'),
      ].find(isVisible);
      if (target instanceof HTMLElement) {
        target.focus();
        return;
      }
    }

    // When the bag is now empty, prefer the empty state's Continue shopping
    // link: it is natively focusable, so it keeps focus through any later
    // re-render of the section, which would strip a scripted tabindex.
    const remainingRows = rows.filter(
      (row) => !removedRows.includes(row) && !row.classList.contains('removing')
    );
    const dialog = root.closest('dialog');
    const fallbacks = [
      remainingRows.length === 0 ? root.querySelector('.cart-items__empty-button') : null,
      root.querySelector('h1, h2, h3'),
      dialog?.querySelector('.nh-cart-close-button, [ref="closeButton"]'),
      dialog ? null : document.querySelector('.cart-page__title h1, .cart-page__title h2, main h1'),
    ];
    const fallback = fallbacks.find(isVisible);
    if (!(fallback instanceof HTMLElement)) return;
    if (!fallback.matches('a[href], button, input, select, textarea, [tabindex]')) {
      fallback.setAttribute('tabindex', '-1');
    }
    fallback.focus();
  }

  /**
   * Writes a message into the page-level polite live region.
   * @param {string | undefined} message
   */
  #announce(message) {
    if (!message) return;
    // Inside the cart drawer the page-level region sits outside the open
    // modal dialog and is silenced, so the drawer gets its own region.
    const dialog = this.closest('dialog');
    let announcer = dialog
      ? dialog.querySelector('[data-cart-items-announcer]')
      : document.getElementById('sr-announcer');
    if (dialog && !announcer) {
      announcer = document.createElement('div');
      announcer.className = 'visually-hidden';
      announcer.setAttribute('role', 'status');
      announcer.setAttribute('aria-live', 'polite');
      announcer.setAttribute('aria-atomic', 'true');
      announcer.setAttribute('data-cart-items-announcer', '');
      dialog.appendChild(announcer);
    }
    if (!announcer) return;
    announcer.textContent = '';
    // Force the live region to register the change even when the text repeats.
    requestAnimationFrame(() => {
      announcer.textContent = message;
    });
  }

  /**
   * Shows a generic "could not update" message on the line and announces it,
   * for failures that never reached a server-side error (network, timeout,
   * unreadable response).
   * @param {number} line
   */
  #showThrownCartError(line) {
    const message = Theme.translations.cart_update_error;
    if (!message) return;

    const quantityInput = this.refs.quantitySelectors?.[line - 1]?.querySelector('input');
    if (quantityInput) quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];
    if (cartItemError instanceof HTMLElement && cartItemErrorContainer instanceof HTMLElement) {
      cartItemError.textContent = message;
      cartItemErrorContainer.classList.remove('hidden');
    }

    this.#announce(message);
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    let succeeded = false;
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    return fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          this.#announce(String(parsedResponseText.errors));
          return;
        }

        succeeded = true;

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], this.isDrawer ? 'hydration' : 'full');

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
        resetShimmer(this);
        this.#showThrownCartError(line);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      })
      .then(() => succeeded);
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
